import React, { useEffect, useState } from "react";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import useNear from "../hooks/nearHook";
import useSbt from "../hooks/sbtHook";
import useSbtPermissions from "../hooks/sbtPermissionsHook";
import useTokenRouting from "../hooks/tokenRoutingHook";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import SbtMetadataTable from "../components/SbtMetadataTable";
import SbtSelector from "../components/SbtSelector";
import SbtTokenListTable from "../components/SbtTokenListTable";
import AccountField from "../components/AccountField";
import SbtPermissionSelector from "../components/SbtPermissionSelector";
import IconButton from "@mui/material/IconButton";
import AddBoxIcon from "@mui/icons-material/AddBox";
import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";
import { useNavigate } from "react-router-dom";

const UserList = ({ accounts, setAccounts, disabled, style = {} }) => {
  return (
    <Grid container spacing={1} style={{ overflow: "auto", ...style }}>
      {(accounts || []).map((account, i) => (
        <Grid item xs={12} key={i}>
          <Grid container>
            <Grid item xs={11}>
              <TextField
                onChange={(e) =>
                  setAccounts((accounts) =>
                    accounts.map((_a, _i) => (_i !== i ? _a : e.target.value))
                  )
                }
                value={account}
                label="AccountId"
                variant="outlined"
                fullWidth
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={1}>
              <IconButton
                onClick={() =>
                  setAccounts((accounts) =>
                    accounts.filter((_a, _i) => _i !== i)
                  )
                }
                disabled={disabled}
              >
                <IndeterminateCheckBoxIcon />
              </IconButton>
            </Grid>
          </Grid>
        </Grid>
      ))}
      <Grid item xs={12}>
        <IconButton
          onClick={() => setAccounts((accounts) => [...accounts, ""])}
          disabled={disabled}
        >
          <AddBoxIcon />
        </IconButton>
      </Grid>
    </Grid>
  );
};

const CreateSbtAuthDashboard = ({}) => {
  const near = useNear();
  const sbt = useSbt();
  const perms = useSbtPermissions();

  const [sbtMetadata, setSbtMetadata] = useState({});
  const [sbtTokens, setSbtTokens] = useState([]);
  const [sbtPermsMetadata, setSbtPermsMetadata] = useState({});
  const [accounts, setAccounts] = useState([]);
  const {
    contractId,
    accountId,
    authContract,
    setContractId,
    setAccountId,
    setAuthContract,
  } = useTokenRouting();
  const [selectedTokens, setSelectedTokens] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!near.account.account) {
      return;
    }
    if (!contractId) {
      setSbtMetadata({});
    }
    sbt.getContractMetadata(contractId).then((result) => {
      setSbtMetadata(result || {});
      if (!result) {
        setSelectedTokens([]);
      }
    });
  }, [contractId, near.account.account]);

  useEffect(() => {
    setAccountId(near.account.account);
  }, [accountId, near.account.account]);

  useEffect(() => {
    if (!contractId || !accountId || !near.account.account) {
      setSbtTokens([]);
      return;
    }
    sbt.getSbtTokensForAccount(contractId, accountId).then((result) => {
      setSbtTokens(result);

      setSelectedTokens([]);
    });
  }, [contractId, accountId, near.account.account]);

  useEffect(() => {
    if (!authContract || !near.account.account) {
      setSbtPermsMetadata({});
      return;
    }
    perms.getContractMetadata(authContract).then(setSbtPermsMetadata);
  }, [authContract, near.account.account]);

  const validAccounts = accounts.map((a) => a.trim()).filter((a) => a);

  const grantPermissions = () => {
    perms.grantPermissions(
      authContract,
      selectedTokens.map((token) =>
        perms.createTokenLocator(contractId, token)
      ),
      validAccounts
    ).then(result => {
      console.log(result);
      navigate(`/sbt-auths/${contractId}/${accountId}/${selectedTokens[0]}/${authContract}`)
    });
  };

  const disabled =
    !selectedTokens.length || !sbtMetadata?.spec || !sbtPermsMetadata?.spec;
  const buttonDisabled = disabled || !validAccounts.length;

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Paper
            sx={{
              p: 2,
              display: "flex",
              flexDirection: "column",
              height: 420,
            }}
          >
            <SbtSelector
              sbtContractId={contractId}
              setSbtContractId={setContractId}
            />
            <SbtMetadataTable sbtMetadata={sbtMetadata} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={7}>
          <Paper
            sx={{
              p: 2,
              display: "flex",
              flexDirection: "column",
              height: 420,
            }}
          >
            <AccountField
              accountId={accountId}
              setAccountId={setAccountId}
              disabled
            />
            <SbtTokenListTable
              sbtTokens={sbtTokens}
              onSelect={(tokenId) => {
                if (selectedTokens.includes(tokenId)) {
                  setSelectedTokens(
                    selectedTokens.filter((_t) => _t !== tokenId)
                  );
                } else {
                  setSelectedTokens([...selectedTokens, tokenId]);
                }
              }}
              selectedTokens={selectedTokens}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper
            sx={{
              p: 2,
              display: "flex",
              flexDirection: "column",
              height: 450,
            }}
          >
            <SbtPermissionSelector
              sbtAuthContractId={authContract}
              setSbtAuthContractId={setAuthContract}
            />
            <SbtMetadataTable sbtMetadata={sbtPermsMetadata} />
            <Button
              fullWidth
              variant="contained"
              onClick={grantPermissions}
              disabled={buttonDisabled}
              style={{ marginTop: 5 }}
            >
              Grant view permission to ({validAccounts.length}) accounts to (
              {selectedTokens.length}) tokens
            </Button>
          </Paper>
        </Grid>
        <Grid item xs={12} md={7}>
          <Paper
            sx={{
              p: 2,
              display: "flex",
              flexDirection: "column",
              height: 450,
            }}
          >
            <UserList
              accounts={accounts}
              setAccounts={setAccounts}
              disabled={disabled}
            />
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CreateSbtAuthDashboard;
